// @cntrl/tokens — the SINGLE typed source of truth for the CNTRL design system. Every value
// below is transcribed VERBATIM from; the `--ac-*` names are byte-diffable against the MVP
// canonical file (`site/css/tokens.css`). The documented, sanctioned deltas vs that file (Layer 1)
// are: the JetBrains Mono stack (RESOLVED, platform-wide self-hosted), the two MINTED status inks
// (--ac-color-danger-ink / --ac-color-info-ink — ratified P-13), the extended z-scale, the new --bp-*
// breakpoints, and the alias collapse (the MVP compat aliases are migration notes, not tokens — their
// VALUES survive under the canonical names), and the DARK-THEME layer (ART-174): a `dark?` value on
// colour/shadow tokens, emitted as a trailing `[data-theme="dark"]` block. Opt-in (attribute selector, never
// prefers-color-scheme); the `:root` light values are unchanged.
// Do NOT invent or drift a value: the generator (generate.ts)
// and the Stylelint/audit step (audit.ts) fail the build on any var(--…) not in this registry.
//
// This module is React-free and framework-free on purpose — the Razor public app vendors the built
// tokens.css artifact, and the generator emits css/tokens.css + scss/_tokens.scss from here.

/** One token: the CSS custom-property name WITHOUT the leading `--`, its value, and an optional note. */
export interface Token {
  readonly name: string;
  readonly value: string;
  readonly note?: string;
  /**
   * An optional DESKTOP override, emitted as `@media (min-width: <md>)`. The base value is therefore the
   * MOBILE one — mobile-first on purpose, so a token whose media query never matches degrades to the
   * touch-safe size rather than the compact one.
   *
   * Use it only where the two contexts genuinely differ by input method, not by taste. It replaces the
   * alternative of shipping two tokens and asking every call site to pick, which puts a desktop-only value
   * one copy-paste away from a phone.
   */
  readonly desktop?: string;
  /**
   * An optional DARK-THEME override, emitted as a trailing `[data-theme="dark"] { … }` block. The base
   * `value` is the LIGHT theme; this is what the token becomes anywhere `data-theme="dark"` is set — at
   * `:root` (a whole-app dark preference) OR on any subtree (the DOS shell forces it on itself).
   *
   * Emitted under the ATTRIBUTE selector only, deliberately NOT `@media (prefers-color-scheme: dark)`: dark is
   * OPT-IN. Nothing flips for a dark-OS user until a page is deliberately switched on, so an unpolished page
   * is never shipped dark by accident.
   *
   * Use it for COLOUR/shadow tokens whose light value is wrong on a dark ground. Marks (platform, roster
   * quad), gradients, the spotlight/on-inverse ramp and the event-kind swatches deliberately carry none —
   * they are already dark-appropriate, or are per-page polish rather than foundation.
   */
  readonly dark?: string;
}

/** A titled group of tokens, in emission order (drives the generated file's section comments). */
export interface TokenSection {
  readonly title: string;
  readonly tokens: readonly Token[];
}

const t = (name: string, value: string, note?: string): Token => ({ name, value, note });

/** A token that changes at the `md` breakpoint: `value` is the MOBILE base, `desktop` the ≥md override. */
const responsive = (name: string, mobile: string, desktop: string, note?: string): Token =>
  ({ name, value: mobile, desktop, note });

/**
 * A token with a DARK-theme override: `light` is the base `:root` value, `dark` is emitted under
 * `[data-theme="dark"]`. See `Token.dark` for the opt-in-only rationale.
 */
const d = (name: string, light: string, dark: string, note?: string): Token =>
  ({ name, value: light, dark, note });

/** The ordered token registry — the source the CSS/SCSS artifacts are generated from. */
export const TOKEN_SECTIONS: readonly TokenSection[] = [
  {
    title: "Color — surfaces",
    tokens: [
      d("ac-color-bg", "#efefee", "#0e0c0b", "Page canvas (cool light gray). Dark: the established dark ground (= --ac-color-inverse)."),
      d("ac-color-glow", "#f5f5f4", "#17130f", "Corner radial wash. Dark: a warm near-black, matching the spotlight gradient's warm start."),
      d("ac-color-paper", "#ffffff", "#1a1614", "Cards, panels, sheets (absorbs aliases card, surface-raised). Dark: a warm surface LIFTED above the canvas."),
      d("ac-color-nested", "#f0ede6", "#221d19", "Nested panels. Dark: one step up from paper."),
      d("ac-color-warm", "#ece9e3", "#241d18", "Soft accent surface."),
      d("ac-color-warm-shadow", "#ddd9d3", "#120f0d", "Warm gradient end."),
      t("ac-color-inverse", "#0e0c0b", "Dark spotlight cards. NO dark override: a dark card is dark in both themes (it is the spotlight ground, and the hero it backs is per-page polish, not foundation)."),
      d("ac-color-sunken", "#e7e7e6", "#080706", "Recessed panels, striped lists. Dark: recessed = below the canvas (the spotlight gradient's darkest stop)."),
      d("ac-color-scrim", "rgba(14,12,11,0.55)", "rgba(0,0,0,0.66)", "Modal/backdrop scrim. Dark: denser, since the page beneath it is already dark."),
      d("ac-color-active", "rgba(139,26,43,0.10)", "rgba(217,74,95,0.22)", "Pressed/selected row. Dark: the brightened accent (see --ac-color-accent) at a higher alpha to read on a dark row."),
      d("ac-color-cool-glow", "#fafafa", "#131210", "The COOL counterpart of --ac-color-glow. Registered rather than written inline (the sheet is hex-free by rule) for the show/hold editor's scoped neutral override, which re-points --ac-color-glow / --ac-color-nested inside those dialogs: the spec's own `.shd-cd` block does the same and records why — 'CD's new VC hold modal is pure white with COOL neutral panels; AC's warm beige nested tone read dirty beside it' (Ryan, 2026-07-20). There is deliberately no third member for the warm slot: that value is #efefee, which is EXACTLY --ac-color-bg, and a second definition of a shipped grey is a drift vector (the --ac-color-roster-quad rule)."),
      d("ac-color-cool-nested", "#f4f4f3", "#1c1a17", "The COOL counterpart of --ac-color-nested — see --ac-color-cool-glow. Distinct from that token rather than folded into it: the override re-points BOTH properties, so one value cannot serve both without the second var() resolving against the already-overridden first."),
    ],
  },
  {
    title: "Color — text ramp",
    tokens: [
      d("ac-color-ink", "#0e0c0b", "#f7f6f4"),
      d("ac-color-body", "#28282a", "#e4e2df"),
      d("ac-color-t2", "#6c6a6a", "#a6a3a0"),
      d("ac-color-t3", "#98979a", "#7b7875", "Restricted to non-essential meta (contrast rule)"),
      d("ac-color-muted", "#aeacae", "#67635f"),
      d("ac-color-disabled", "#c5c4c4", "#4a4643"),
      t("ac-color-on-accent", "#ffffff", "Text on the accent fill — white in both themes (the accent stays a saturated crimson/rose)."),
      t("ac-color-text-subtle", "rgba(255,255,255,0.7)", "Text on dark — already a white alpha, so unchanged by theme."),
    ],
  },
  {
    title: "Color — lines & focus",
    tokens: [
      d("ac-color-line", "rgba(0,0,0,0.07)", "rgba(255,255,255,0.10)", "Default borders. Dark: a white alpha, since a black hairline is invisible on a dark ground."),
      d("ac-color-line-strong", "rgba(0,0,0,0.12)", "rgba(255,255,255,0.16)", "Input borders (1.5px)"),
      d("ac-color-line-subtle", "rgba(0,0,0,0.04)", "rgba(255,255,255,0.06)", "Nested dividers"),
      d("ac-color-ring", "rgba(139,26,43,0.32)", "rgba(217,74,95,0.55)", "Focus ring color. Dark: the brightened accent at a higher alpha for a visible ring on near-black."),
    ],
  },
  {
    title: "Color — accent (AC Crimson)",
    tokens: [
      // Dark BRIGHTENS the accent to #d94a5f — brand crimson lightened lands on the coral the dark hero already
      // speaks (--ac-color-spotlight-figure #ff9b8f / --ac-color-spotlight-down #ff9b95). #8B1A2B as a link or a
      // thin border on near-black is ~3:1; the lighter tone clears AA for text and reads as a border. The FILL
      // stays saturated (white-on-accent is legible either way), so buttons keep their weight.
      d("ac-color-accent", "#8B1A2B", "#d94a5f"),
      t("ac-color-brand", "#8B1A2B", "The brand crimson, FIXED in both themes — for a solid brand fill on a dark surface (the mobile DOS tab square), where --ac-color-accent's brightened coral reads as a different red (PO, ART-251). Text and links on dark keep the accent."),
      d("ac-color-accent-hi", "#a82238", "#e56475", "hover"),
      d("ac-color-accent-chip", "rgba(139,26,43,0.12)", "rgba(217,74,95,0.20)"),
      d("ac-color-accent-glow", "rgba(139,26,43,0.35)", "rgba(217,74,95,0.40)"),
      d("ac-color-accent-wash", "rgba(139,26,43,0.06)", "rgba(217,74,95,0.10)"),
      d("ac-color-accent-rgb", "139, 26, 43", "217, 74, 95", "triplet for consumer-composed alpha (::selection, etc.) — tracks the dark accent so composed washes brighten with it"),
    ],
  },
  {
    title: "Color — party chip (the SECOND tag tone; PO 2026-09-11)",
    tokens: [
      t(
        "ac-color-party-chip",
        "rgba(47,143,143,0.16)",
        "Touring-party tag fill — VERBATIM from the messaging handoff's own stylesheet (.tag--party, ART-159). A SECOND chip tone, because the inbox row carries two tags at once (SHOW + A PARTY) and two crimson chips side by side read as one control rather than two facts. A WASH like the accent chip, not a flat hex, so it sits correctly on the sunken ground of a selected row as well as on paper. NOTE the spec also fixes .tag--tour rgba(20,20,20,.09)/#2a2a2a and .tag--bus rgba(201,138,43,.18)/#7d5009 — unminted, because neither kind exists yet.",
      ),
      t(
        "ac-color-party-ink",
        "#1d5e5e",
        "Touring-party tag ink — the handoff's own value; 6.2:1 on its own chip over paper, so the 9px uppercase label clears AA at the one size it is ever set in.",
      ),
      t(
        "ac-color-bus-chip",
        "rgba(201,138,43,0.18)",
        "Bus-chat tag fill (ART-160) — VERBATIM from the messaging handoff's own stylesheet (.tag--bus), minted now that bus chats exist. A THIRD chip tone (amber), so a coach chat's kind chip is not the crimson a show chat's is: the inbox lists both, and one tone for both would say they are the same kind of thing.",
      ),
      t(
        "ac-color-bus-ink",
        "#7d5009",
        "Bus-chat tag ink — the handoff's own value; clears AA on its own amber chip at the 9px uppercase size the label is set in.",
      ),
    ],
  },
  {
    title: "Color — status ramp (danger-ink/info-ink MINTED, ratified P-13)",
    tokens: [
      // The status HUES and their low-alpha BG washes read on either ground; only the INKS flip. The light
      // inks are dark on purpose (text on a pale wash) and are near-invisible on a dark card, so dark lifts
      // each to the ~400 tint of its hue.
      t("ac-color-success", "#22c55e"),
      t("ac-color-success-bg", "rgba(34,197,94,0.12)"),
      d("ac-color-success-ink", "#157a3a", "#4ade80"),
      t("ac-color-warning", "#f9a825"),
      t("ac-color-warning-bg", "rgba(249,168,37,0.12)"),
      d("ac-color-warning-ink", "#a86b00", "#fbbf24"),
      // DANGER IS THE BRAND CRIMSON (PO, ART-253, 2026-10-05): "the red button in delete case is the wrong red". It was a
      // separate generic red (#ef4444) so crimson stayed off action buttons (BRAND-3); the PO wants ONE red, so every
      // destructive control, error and failed state now reads in crimson. Dark brightens it, but NOT to the accent's
      // #d94a5f: a danger confirm is a FILL carrying white text, and white on #d94a5f is 4.12:1 (under AA). #c63d53 is
      // 5.01:1 under white and 3.9:1 against the dark ground. Light: white on #8B1A2B 9.21:1; ink on paper 7.12:1.
      d("ac-color-danger", "#8B1A2B", "#c63d53"),
      d("ac-color-danger-bg", "rgba(139,26,43,0.10)", "rgba(198,61,83,0.16)"),
      d("ac-color-danger-ink", "#a82238", "#e56475", "MINTED (P-13) — the MVP ships no danger ink. Crimson since ART-253; also the danger confirm's hover."),
      t("ac-color-info", "#3b82f6"),
      t("ac-color-info-bg", "rgba(59,130,246,0.12)"),
      d("ac-color-info-ink", "#1d4ed8", "#60a5fa", "MINTED (P-13) — the MVP ships no info ink"),
      t("ac-color-review", "#8b5cf6", "MINTED — the NEEDS REVIEW task status (purple)"),
      t("ac-color-review-bg", "rgba(139,92,246,0.12)"),
      d("ac-color-review-ink", "#6d28d9", "#a78bfa"),
    ],
  },
  {
    title: "Color — the All-roster quad mark (the roster rail's four-swatch glyph, and nowhere else)",
    tokens: [
      t("ac-color-roster-quad-1", "#2f8f8f", "Teal. The four swatches are a fixed brand quad ported from the legacy roster rail's `.rv-all__tile`, standing for a portfolio of artists rather than one — so they are a MARK, like the per-platform brand colors above, not a categorical palette to reach for elsewhere. Registered rather than written inline because the sheet is hex-free by rule; the FOURTH quadrant is deliberately absent, because it is exactly --ac-color-accent and a second definition of the brand crimson is a drift vector."),
      t("ac-color-roster-quad-2", "#3a4ed8", "Blue."),
      t("ac-color-roster-quad-3", "#c98a2b", "Gold."),
    ],
  },
  {
    title: "Color — platform accents (only inside per-platform UI)",
    tokens: [
      t("ac-color-p-spotify", "#22c55e"),
      t("ac-color-p-instagram", "#ec407a"),
      t("ac-color-p-facebook", "#1d4ed8"),
      t("ac-color-p-youtube", "#ef4444"),
      t("ac-color-p-tidal", "#000000"),
      t("ac-color-p-tiktok", "#0e0c0b"),
      t("ac-color-p-threads", "#0e0c0b"),
      t("ac-color-p-amazon", "#22d3ee"),
      t("ac-color-p-apple", "#fa233b", "Apple Music. Brand-guide ruling 2026-08-13: NOT the #ec407a Instagram pink the old fallback shipped (guide DRIFT-4)."),
      t("ac-color-p-pandora", "#00a0ee"),
      // Added 2026-08-17 for the Link-in-Bio platform picker. The provided guide names no hex for these,
      // so the source is each brand's own mark colour; black-marked brands take our warm ink (the
      // tiktok/threads precedent) rather than #000000.
      t("ac-color-p-bandcamp", "#408294"),
      t("ac-color-p-soundcloud", "#ff5500"),
      t("ac-color-p-x", "#0e0c0b"),
      t("ac-color-p-discord", "#5865f2"),
      t("ac-color-p-twitch", "#9146ff"),
      // Added 2026-08-26 for the show preview's "Push to Bandsintown" control, which carries the brand mark
      // on an ink pill. Same source rule as the 2026-08-17 block above — the brand's own mark colour, which
      // for Bandsintown is the teal Simple Icons vendors alongside the path in `PlatformIcon`'s marks.
      t("ac-color-p-bandsintown", "#00cec8"),
      // Added 2026-11-03 for the release-file import's source rows (ART-369). Same source rule: each brand's own
      // mark colour, as Simple Icons vendors it beside the path.
      t("ac-color-p-dropbox", "#0061ff"),
      t("ac-color-p-box", "#0061d5"),
      t("ac-color-p-google-drive", "#4285f4"),
    ],
  },
  {
    // Text/fills that sit ON the dark spotlight hero (--ac-color-inverse). The MVP composed these inline; the
    // rewrite tokenises them so the SAME dark hero can be reused by Releases/Music/Content (FE-SH-02).
    title: "Color — dark hero / spotlight (on-inverse surfaces)",
    tokens: [
      t("ac-color-on-inverse-strong", "rgba(255,255,255,0.72)", "Primary meta text on the dark hero"),
      t("ac-color-on-inverse-muted", "rgba(255,255,255,0.62)", "Eyebrow on the dark hero"),
      t("ac-color-on-inverse-subtle", "rgba(255,255,255,0.55)", "Tile label on the dark hero"),
      t("ac-color-spotlight-tile", "rgba(255,255,255,0.055)", "Stat-tile fill on the dark hero"),
      t("ac-color-spotlight-tile-line", "rgba(255,255,255,0.09)", "Stat-tile border on the dark hero"),
      t(
        "ac-color-on-bleed-strong",
        "rgba(255,255,255,0.92)",
        "Primary meta text over a COVER-ART bleed — the on-inverse ramp stepped up, because that ground is the band's artwork rather than a gradient we chose",
      ),
      t(
        "ac-color-on-bleed-muted",
        "rgba(255,255,255,0.85)",
        "Eyebrow / meta over a COVER-ART bleed",
      ),
      t(
        "ac-color-on-bleed-subtle",
        "rgba(255,255,255,0.80)",
        "Tile + count labels over a COVER-ART bleed",
      ),
      t(
        "ac-color-spotlight-rule",
        "rgba(255,255,255,0.25)",
        "Hairline between INLINE items on the dark hero (the release meta line) — heavier than the tile border, which rings a box rather than parting two words",
      ),
      t(
        "ac-color-spotlight-figure",
        "#ff9b8f",
        "The one highlighted inline figure on the dark hero (the release countdown). Not spotlight-down: it marks the value that MOVES, never a negative delta",
      ),
      // Stronger than spotlight-tile on purpose: a chip must read as an OBJECT over an arbitrary photo
      // (the profile hero), where the .055 tile fill disappears. Chosen against the reference at
      // .12/.16/.22 side by side (crew profile modal, 2026-08-20).
      t("ac-color-inverse-chip", "rgba(255,255,255,0.16)", "Glass chip fill on dark/imagery"),
      t("ac-color-inverse-chip-line", "rgba(255,255,255,0.28)", "Glass chip border on dark/imagery"),
      t("ac-color-spotlight-up", "#7ef0a6", "Positive delta ink on dark"),
      t("ac-color-spotlight-up-line", "rgba(34,197,94,0.30)", "Positive delta pill border on dark"),
      t("ac-color-spotlight-down", "#ff9b95", "Negative delta ink on dark"),
      t("ac-color-spotlight-down-line", "rgba(239,68,68,0.30)", "Negative delta pill border on dark"),
    ],
  },
  {
    // Schedule event-kind markers for the ops Calendar grid + legend. THE VALUES ARE THE DESIGN SPEC'S OWN
    // LEGEND (`schedule-page.decoded.html`, the `.cal-legend` swatches — the key the operator re-sent on
    // 2026-09-09), transcribed here and nowhere else. The spec's standing defect on that map is that it was a
    // TypeScript object of raw hexes duplicating the brand crimson a tenth time — the fix it prescribes is
    // exactly this file, so the VALUES are ported and the mechanism is not. SHOW is `--ac-color-accent`, which
    // IS the key's #8B1A2B, referenced rather than retyped so the two can never drift.
    //
    // THE KEY HAS SEVEN SWATCHES FOR EIGHT KINDS, and both halves of that mismatch are deliberate:
    //
    //   OFF and TRAVEL ARE ONE THING (operator, 2026-09-09). Two `TourDay.Kind` values, one swatch — the key
    //   prints it "OFF / TRAVEL", the one legend label that is not just an uppercased kind. So `travel` is an
    //   ALIAS of `off` rather than a second slate: a copied hex is a hex someone can change on one line, and a
    //   rest day and a drive day reading as two different colours is exactly what the merged label denies. The
    //   two stay distinct KINDS (the rail still says TRAVEL over "Nashville → Austin", OFF over "Day off in
    //   Houston") — what they share is the colour, which is all a legend is.
    //
    //   FESTIVAL is minted, because the key has no entry for it: it is a real `Show.Kind` our model splits out
    //   of `show` and legacy's vocabulary does not have (the audit's H2 — the two sets are not a subset of each
    //   other either way). It takes GREEN, the one hue the key leaves unspent. NOT the blue: the key spends
    //   that on TOUR, a legacy opportunity type our own vocabulary does not carry (`Types` is rehearsal /
    //   content / pr / other), so there is no TOUR row to render — and re-using its blue would collide the day
    //   one is added.
    //
    // These are BAR FILLS now, not 6px dots, so each carries text — and the three LIGHT fills take ink rather
    // than white: measured against white, hold #d98324 is 2.9:1, content #1f9e93 3.3:1 and pr #d8568f 3.7:1,
    // all fails at 11px. The fills are the key's regardless; it is the TEXT that adapts, because darkening a
    // fill would put the legend swatch and the bar it explains on two different colours.
    title: "Color — schedule event kinds",
    tokens: [
      t("ac-color-event-show", "var(--ac-color-accent)", "Show day — AC crimson #8B1A2B (CD legend SHOW)"),
      t("ac-color-event-festival", "#15803d", "Festival day — green (minted; the one hue the CD key leaves free)"),
      t("ac-color-event-hold", "#d98324", "Hold day — amber (CD legend HOLD); takes ink text"),
      t("ac-color-event-rehearsal", "#7b4dd8", "Rehearsal / write day — violet (CD legend REHEARSAL)"),
      t("ac-color-event-content", "#1f9e93", "Content day — teal (CD legend CONTENT); takes ink text"),
      t("ac-color-event-pr", "#d8568f", "PR / press / placement — pink (CD legend PR); takes ink text"),
      t("ac-color-event-off", "#5b6674", "Off day — slate (CD legend OFF / TRAVEL); rest days recede"),
      t("ac-color-event-travel", "var(--ac-color-event-off)", "Travel day — the SAME swatch as off, by ruling"),
    ],
  },
  {
    // Composed multi-layer backgrounds (kept as tokens so component SCSS stays hex-free, firewall).
    title: "Gradients — composed backgrounds",
    tokens: [
      t(
        "ac-gradient-spotlight",
        "radial-gradient(120% 140% at 82% -10%, rgba(168,34,56,0.55) 0%, rgba(139,26,43,0) 52%), radial-gradient(90% 120% at 8% 110%, rgba(40,30,28,0.9) 0%, rgba(14,12,11,0) 60%), linear-gradient(125deg,#171311 0%,#0e0c0b 60%,#080706 100%)",
        "The dark spotlight hero background",
      ),
      t(
        "ac-gradient-spotlight-hatch",
        "repeating-linear-gradient(115deg,rgba(255,255,255,0.025) 0 2px,transparent 2px 9px)",
        "Diagonal hatch overlay (::after) on the spotlight hero",
      ),
      t(
        "ac-gradient-spotlight-scrim",
        "linear-gradient(135deg,rgba(26,14,13,0.92) 0%,rgba(45,21,24,0.78) 45%,rgba(139,26,43,0.55) 100%)",
        "Readability scrim laid over a band press photo on the spotlight hero",
      ),
      t(
        "ac-gradient-spotlight-bleed-scrim",
        "linear-gradient(110deg,rgba(16,9,8,0.80) 0%,rgba(16,9,8,0.58) 55%,rgba(16,9,8,0.72) 100%)",
        "Readability scrim over a blurred COVER-ART bleed. DIRECTIONAL — heavy where the type sits, light through the middle, so the artwork stays visible",
      ),
      t(
        "ac-gradient-flyer",
        "linear-gradient(135deg,#2a0e16 0%,#0e0c0b 100%)",
        "Empty show-flyer tile — the crimson-to-ink poster ground",
      ),
      t(
        "ac-gradient-flyer-hatch",
        "repeating-linear-gradient(45deg,transparent 0 16px,rgba(255,255,255,0.04) 16px 17px)",
        "Diagonal hairline stripes laid over the empty flyer tile",
      ),
      t(
        "ac-gradient-instagram",
        "linear-gradient(135deg,#f9ce34,#ee2a7b 48%,#6228d7)",
        "Instagram brand gradient (platform badge fill)",
      ),
    ],
  },
  {
    title: "Typography — families (JetBrains Mono platform-wide, self-hosted)",
    tokens: [
      t("ac-font-display", "'Archivo Black','Inter',system-ui,sans-serif", "Display ONLY, weight 900"),
      t("ac-font-body", "'Inter',system-ui,-apple-system,sans-serif", "All readable text/UI"),
      t("ac-font-mono", "'JetBrains Mono',ui-monospace,SFMono-Regular,Menlo,Consolas,monospace"),
    ],
  },
  {
    title: "Typography — weight scale",
    tokens: [
      t("ac-fw-regular", "400"),
      t("ac-fw-medium", "500"),
      t("ac-fw-semibold", "600"),
      t("ac-fw-bold", "700"),
      t("ac-fw-black", "900"),
    ],
  },
  {
    title: "Typography — size scale",
    tokens: [
      t("ac-fs-2xs", "9px"),
      t("ac-fs-xs", "10px"),
      t("ac-fs-sm", "11px"),
      t("ac-fs-base", "12px", "root UI size"),
      t("ac-fs-md", "13px"),
      t("ac-fs-lg", "14px", "default body"),
      t("ac-fs-xl", "16px"),
      t("ac-fs-2xl", "18px"),
      t("ac-fs-3xl", "22px"),
      t("ac-fs-4xl", "26px"),
      t("ac-fs-5xl", "32px"),
      // HUGE — deliberately OUTSIDE the 2xs..5xl ramp, not `6xl`, and the name is the point. That ramp is the
      // interface scale: each step is the next heading or body size, so a `6xl` invites use as "the next
      // heading down from nothing" and would drift into ordinary layout. This is one job — a full-bleed hero
      // figure alone on a card — and at 5xl (32px, what a KPI tile uses) it reads as one more stat rather
      // than as the number the page is about. Named `huge` rather than `display` because --ac-font-display
      // already means the display FAMILY, and a size that echoed it would read as "the display font's size".
      t("ac-fs-huge", "64px", "hero figures only — outside the UI ramp, never a heading"),
    ],
  },
  {
    title: "Typography — tracking & leading",
    tokens: [
      t("ac-tracking-tighter", "-0.02em"),
      t("ac-tracking-tight", "-0.01em"),
      t("ac-tracking-normal", "0"),
      t("ac-tracking-wide", "0.04em"),
      t("ac-tracking-wider", "0.08em"),
      t("ac-tracking-widest", "0.14em"),
      t("ac-tracking-mega", "0.18em"),
      t("ac-leading-tight", "1.2"),
      t("ac-leading-snug", "1.35"),
      t("ac-leading-normal", "1.45"),
      t("ac-leading-relaxed", "1.6"),
    ],
  },
  {
    title: "Spacing — 4px grid, non-contiguous",
    tokens: [
      t("ac-space-0", "0"),
      t("ac-space-1", "4px"),
      t("ac-space-2", "8px"),
      t("ac-space-3", "12px"),
      t("ac-space-4", "16px"),
      t("ac-space-5", "20px"),
      t("ac-space-6", "24px"),
      t("ac-space-8", "32px"),
      t("ac-space-10", "40px"),
      t("ac-space-14", "56px"),
    ],
  },
  {
    title: "Radius",
    tokens: [
      t("ac-radius-xs", "4px"),
      t("ac-radius-sm", "8px"),
      t("ac-radius-md", "12px"),
      t("ac-radius-lg", "16px"),
      t("ac-radius-xl", "20px"),
      t("ac-radius-2xl", "24px"),
      t("ac-radius-3xl", "28px"),
      t("ac-radius-pill", "999px"),
      t("ac-radius-circle", "50%"),
    ],
  },
  {
    title: "Elevation — warm-tinted shadows",
    tokens: [
      // The warm shadows (rgba(50,20,15,…)) vanish on a dark ground — the shadow colour ≈ the canvas. Dark
      // switches to a black-based, more opaque shadow so a lifted card still separates. The inset top-light
      // drops from a near-opaque white to a whisper (a bright top edge on a dark card reads as a seam).
      d("ac-shadow-sm", "0 1px 3px rgba(50,20,15,0.06)", "0 1px 3px rgba(0,0,0,0.5)", "chips/inputs"),
      d("ac-shadow-card", "0 10px 30px -15px rgba(50,20,15,0.08)", "0 10px 30px -15px rgba(0,0,0,0.6)", "workhorse panel"),
      d("ac-shadow-card-hi", "0 22px 50px -22px rgba(50,20,15,0.22)", "0 22px 50px -22px rgba(0,0,0,0.7)", "spotlight hero + card hover lift"),
      d("ac-shadow-md", "0 10px 30px -15px rgba(50,20,15,0.12)", "0 10px 30px -15px rgba(0,0,0,0.65)", "raised/hover"),
      d("ac-shadow-lg", "0 30px 80px -30px rgba(50,20,15,0.25)", "0 30px 80px -30px rgba(0,0,0,0.8)", "modals/overlays"),
      d("ac-shadow-accent", "0 4px 14px -4px rgba(50,20,15,0.4)", "0 4px 14px -4px rgba(0,0,0,0.6)", "under crimson elements"),
      d("ac-shadow-inset", "inset 0 1px 0 rgba(255,255,255,0.9)", "inset 0 1px 0 rgba(255,255,255,0.05)", "top-light on raised fills"),
    ],
  },
  {
    title: "Motion (all motion honors prefers-reduced-motion)",
    tokens: [
      t("ac-duration-fast", "120ms", "hovers/presses"),
      t("ac-duration-base", "200ms", "most transitions/tab switches"),
      t("ac-duration-slow", "300ms", "reveals/drawer slides"),
      t("ac-ease-standard", "cubic-bezier(0.2,0,0,1)"),
      t("ac-ease-emphasized", "cubic-bezier(0.3,0,0,1.2)", "overshoot — elements that APPEAR"),
    ],
  },
  {
    title: "Controls — single-line input/select heights. Textareas set their own and grow.",
    tokens: [
      responsive("ac-control-fs", "14px", "12px", "Control text size, paired with --ac-control-h so the type tracks the box instead of being chosen per stylesheet. Today the same 28px box holds 14px text on the shared field and 11px in the dense rows; one token ends that."),
      responsive("ac-control-h", "44px", "28px", "Single-line control height — ONE token, sized by input method rather than by surface. Touch gets 44px (the guide's §14 target); a mouse gets 28px, which is what dense card rows want. Mobile is the BASE so the touch-safe value is what you get if the query never matches, and no call site can put a compact control on a phone."),
      t("ac-control-pad-inline", "16px", "Single-line control INLINE padding — the third knob beside --ac-control-h and --ac-control-fs, and externalised for the same reason they were: it is the one box dimension that was still a literal in Field.module.scss, so a surface wanting the reference's proportion had no way to ask for it except by forking the primitive. The default is the shipped 16px, so nothing moves by adding it. It matters because padding reads RELATIVE to the box: 16px inside a 28px desktop control is a 0.57 ratio where the show editor's reference is 14px inside a ~39px one (0.36), which is what 'too much space on the left of the text in each box' (ART-87) describes. A scope that re-points it should pick a value from the same ratio, not a taste."),
    ],
  },
  {
    title: "Layout",
    tokens: [
      t("ac-container-max", "1680px"),
      t("ac-container-pad", "24px"),
      t("ac-shell-pad", "40px", "Inline inset of a shell's page CONTENT — the routed <main>, which is also capped and centred. NOT the chrome inset: the top bar and the roster rail are pinned to the viewport edge and read --ac-shell-edge-pad instead."),
      t("ac-shell-edge-pad", "12px", "Inline inset of chrome that is PINNED TO THE VIEWPORT EDGE — the full-bleed top bar's two ends and the roster rail's own padding. It is deliberately much tighter than --ac-shell-pad (which insets centred page content) and it is ONE token because the two have to agree: the rail sits directly beneath the bar's leading lockup, so a bar inset to the content pad put the app logo 28px right of the sidebar edge it hangs over."),
      t("ac-shell-topbar-h", "55px", "Shell top bar height — ONE value for every shell, so the chrome does not move when you cross between them. It had been emergent (no height anywhere; the bar was as tall as its tallest child), which made it a property of the RIGHT-hand cluster: the roster's two-line account chip rendered 66.9px against the artist shell's 65px, and the roster rail's sticky offset was a hard-coded 57px that had never matched either. Includes the bottom hairline (everything is border-box), so it is also what a sticky offset below the bar subtracts. 55px is a DELIBERATE trim (PO, 2026-09-01), not a fit: the account chip was resized to ~34px to sit inside it rather than the bar being sized around whatever the chip happened to be — which is the inversion this token exists to prevent."),
    ],
  },
  {
    title: "Z-index — extended scale (ADDITIVE)",
    tokens: [
      t("ac-z-base", "0"),
      t("ac-z-raised", "10"),
      t("ac-z-sticky", "100", "topbar, sticky headers"),
      t("ac-z-overlay", "200", "scrims, popovers, dropdowns"),
      t("ac-z-drawer", "300", "settings drawer, sheets"),
      t("ac-z-modal", "400", "modals"),
      t("ac-z-dialog", "500", "ac-dialog confirms (stack over modals)"),
      t("ac-z-gate", "600", "auth/paywall gate"),
      t("ac-z-toast", "700", "toasts (always topmost)"),
    ],
  },
  {
    title: "Breakpoints — NEW tokens (--bp-* namespace, not --ac-*)",
    tokens: [
      t("bp-sm", "480px"),
      t("bp-md", "720px"),
      t("bp-lg", "900px"),
      t("bp-xl", "1180px"),
      t("bp-max", "1680px"),
    ],
  },
  {
    // The mobile shell layer (verbatim from). A distinct `--m-*`/
    // `--dos-*` namespace (like `--bp-*`, not `--ac-*`) — these are mobile-shell layout tokens, not brand
    // tokens; the two that echo brand values reference the canonical `--ac-*` token so they never drift.
    title: "Mobile shell layer (--m- and --dos- namespaces, not --ac-)",
    tokens: [
      t("m-gutter", "16px", "MobileShell horizontal gutter"),
      t("m-section", "24px", "vertical section rhythm"),
      t("m-radius-card", "var(--ac-radius-lg)", "= --ac-radius-lg (16px)"),
      t("m-radius-pill", "999px"),
      t("m-tabbar-h", "76px", "bottom tab bar content height (icons + labels, top-aligned); the home-indicator reservation is added separately at each call site as --m-tabbar-safe"),
      t("m-tabbar-safe", "max(env(safe-area-inset-bottom) - 18px, 0px)", "home-indicator reservation below the tab bar — a REDUCED share of the safe-area inset (the bar is chrome and its background already extends to the screen edge, so it needs less clearance than page content); 0 on devices with no home indicator"),
      t("m-header-h", "52px", "persistent mobile header height (brand mark + bell + account avatar)"),
      t("dos-accent", "var(--ac-color-accent)", "DOS/center-button accent = crimson"),
      // The Day-of-Show flagship palette (ART-174). DOS is an ALWAYS-DARK surface (forced via app/theme.ts),
      // so these are fixed brand values, not theme-variant pairs — verbatim from the shipped m-day-of-show.css
      // (see .design-specs/dos-mobile.audit.md). They live here because the SCSS sheet is hex-free by rule.
      t("dos-bg", "#0e0c0b", "DOS ground"),
      t("dos-surface", "rgba(255,255,255,0.04)", "DOS card / tile surface"),
      t("dos-surface-strong", "rgba(255,255,255,0.08)", "DOS raised surface / pill / progress track"),
      t("dos-text", "#fafaf8", "DOS primary text"),
      t("dos-text-dim", "rgba(255,255,255,0.55)", "DOS dim text (labels)"),
      t("dos-text-tertiary", "rgba(255,255,255,0.4)", "DOS tertiary text"),
      t("dos-text-faint", "rgba(255,255,255,0.3)", "DOS faint text"),
      t("dos-border", "rgba(255,255,255,0.06)", "DOS hairline"),
      t("dos-border-strong", "rgba(255,255,255,0.16)", "DOS strong hairline / chip outline"),
      t("dos-accent-hi", "#a82238", "DOS canonical crimson — fixed on the always-dark surface, NOT the theme's brightened accent"),
      t("dos-glow", "rgba(214,58,82,0.35)", "DOS Now-card radial glow"),
      t("dos-glow-soft", "rgba(214,58,82,0.25)", "DOS soft glow / accent border / accent-dot halo"),
      t("dos-card-top", "#1a1614", "DOS Now / today-card gradient start"),
      t("dos-card-bottom", "#15110f", "DOS Now / today-card gradient end"),
      t("dos-live", "#4ade80", "DOS live dot — green"),
      t("dos-live-halo", "rgba(74,222,128,0.25)", "DOS live-dot halo"),
      t("dos-hot", "#ff5e7a", "DOS progress-fill hot end"),
      t("dos-white", "#ffffff", "Pure white — the Now-card title + live-row emphasis on the dark ground"),
      t("dos-info", "#6ea8fe", "DOS logistics action — the blue secondary-nav accent"),
    ],
  },
];

/** Flat map of every token: full CSS var name (with leading `--`) → value. */
export const TOKENS: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(TOKEN_SECTIONS.flatMap((s) => s.tokens.map((tok) => [`--${tok.name}`, tok.value]))),
);

/** Every registered CSS var name (with leading `--`). The audit's allowlist. */
export const TOKEN_NAMES: readonly string[] = Object.freeze(Object.keys(TOKENS));

/** The breakpoint pixel values, for the SCSS map + the useBreakpoint hook.*/
export const BREAKPOINTS = Object.freeze({
  sm: 480,
  md: 720,
  lg: 900,
  xl: 1180,
  max: 1680,
} as const);

export type BreakpointName = keyof typeof BREAKPOINTS;
